/**
 * Content Renderer - Single source of truth for rendering question content
 *
 * Consolidates the duplicated renderContent() function from 15+ components
 * into a shared module with a processing pipeline:
 *
 *   Raw Content → [Repair] → [Extract Shortcodes] → [Sanitize] → [Restore] → [Render]
 *
 * Backward-compatible API: renderContent(content, contentType, sanitizerOrOptions)
 */

import React from 'react'
import katex from 'katex'
import { BlockMath } from 'react-katex'
import {
  renderMarkdownWithMath,
  renderMathContent,
  findDollarMathExpressions
} from '../components/MathRenderer'
import { isHTML } from './htmlDetection'
import { sanitizeQuestionHTML, sanitizeAnswerHTML } from './sanitization'
import { wrapBareDataUriImages, BARE_DATA_URI_RE } from '@shared/content/dataUriImages.js'
import {
  repairCorruptedLatex,
  convertAlttextToLatex,
  convertMathMLToLatex,
  escapeBareMathPercent,
  restoreBareLatexCommands,
  ALTTEXT_TRIGGER_RE
} from './latexRepair'
import { extractShortcodes, hasShortcodes } from './shortcodeParser'
import ShortcodeRenderer from '../components/renderers/ShortcodeRenderer'
import { createLogger } from './logger'

const logger = createLogger('contentRenderer')

// ---------------------------------------------------------------------------------------------
// Pipeline guards (CONTENT-2): a size cap and fast-path skips so the repair regexes and the
// math-detection passes stay bounded on the render path. Content above the cap is rendered
// untransformed (with a logged warning) instead of being fed to multi-pass regex repair.
// ---------------------------------------------------------------------------------------------

// ~200KB. Real question content (even with large tables/SVG) stays far below this; anything
// bigger is pathological and must not hit the repair/math regex passes on every render.
export const MAX_PROCESS_LENGTH = 200_000

// Non-whitespace control characters repaired by repairCorruptedLatex (its own trigger set).
// eslint-disable-next-line no-control-regex
const CORRUPTED_LATEX_MARKER_RE = /[\x00-\x08\x0B\x0C]/

/**
 * Cheap signal that any processContent pass could change the text. Mirrors, one-for-one, the
 * trigger condition of each pipeline phase below:
 *   - control chars            → repairCorruptedLatex
 *   - <math                    → convertMathMLToLatex
 *   - alttext keyword          → convertAlttextToLatex
 *   - {{DESCRIPTION}}          → TIKZ extraction + description stripping (both need the block)
 *   - <keyStrategy>            → keyStrategy stripping
 * If none match, every pass is a no-op, so the pipeline can be skipped entirely.
 */
function hasProcessMarkers(text) {
  return (
    CORRUPTED_LATEX_MARKER_RE.test(text) ||
    /<math/i.test(text) ||
    text.includes('{{DESCRIPTION}}') ||
    /<keyStrategy>/i.test(text) ||
    ALTTEXT_TRIGGER_RE.test(text)
  )
}

/**
 * Run the content processing pipeline: repair → alttext conversion
 * Exported for stage-level tests (contentPipeline.fixtures.test.js).
 * @param {string} text - Raw content string
 * @returns {string} Processed content string
 */
export function processContent(text) {
  if (!text || typeof text !== 'string') return text

  // Guard 1: size cap — pass oversized content through untransformed rather than running
  // multi-pass regex repair over it on the render path.
  if (text.length > MAX_PROCESS_LENGTH) {
    // Kept as a bare console.warn: contentRenderer.test.jsx pins this guard's warning
    // shape verbatim (single-argument assertion), so the logger's '[context]' prefix
    // would break it. All four size-cap guards keep the same shape for consistency.
    // eslint-disable-next-line no-console
    console.warn(
      `processContent: content length ${text.length} exceeds cap ${MAX_PROCESS_LENGTH}; skipping repair passes`
    )
    return text
  }

  // Guard 2: fast path — clean content (no repair markers) skips every pass.
  if (!hasProcessMarkers(text)) {
    return text
  }

  // Phase 1: Repair corrupted LaTeX escape sequences
  let processed = repairCorruptedLatex(text)

  // Phase 1b: Convert MathML <math alttext="..."> elements to inline LaTeX ($...$)
  // This must run BEFORE convertAlttextToLatex so that alttext English descriptions
  // (e.g., "StartFraction") extracted from <math> tags can be further converted.
  processed = convertMathMLToLatex(processed)

  // Phase 1b2: Convert remaining alttext-style English descriptions to LaTeX
  processed = convertAlttextToLatex(processed)

  // Phase 1d: Extract [TIKZ] blocks from {{DESCRIPTION}} before stripping.
  // Questions use {{IMAGE:N}} as placeholder and embed TikZ source inside {{DESCRIPTION}}.
  // Convert the pair into a {{TIKZ: ...}} shortcode so TikZRenderer picks it up.
  processed = processed.replace(
    /\{\{IMAGE:\s*(\d+)\s*\}\}\s*\{\{DESCRIPTION\}\}[\s\S]*?\[TIKZ\]([\s\S]*?)\[\/TIKZ\][\s\S]*?\{\{\/DESCRIPTION\}\}/g,
    (_, imageNum, tikzCode) => `{{TIKZ: image${imageNum} | code: ${tikzCode.trim()}}}`
  )

  // Strip remaining {{DESCRIPTION}}...{{/DESCRIPTION}} blocks (pure alt text, no TikZ)
  processed = processed.replace(/\{\{DESCRIPTION\}\}[\s\S]*?\{\{\/DESCRIPTION\}\}/g, '')

  // Phase 1e: Strip <keyStrategy>...</keyStrategy> tags (rationale metadata, not for display)
  processed = processed.replace(/<keyStrategy>[\s\S]*?<\/keyStrategy>/gi, '')

  return processed
}

// Pattern to find block-level math: $$...$$ or \[...\]
const BLOCK_MATH_RE = /\$\$([\s\S]+?)\$\$|\\\[([\s\S]+?)\\\]/g

// Pattern to find \(...\) inline math (non-greedy to handle inner parentheses like \(f(x)\))
const PAREN_MATH_RE = /\\\((.+?)\\\)/gs

/**
 * Render an inline math expression to a KaTeX HTML string.
 * Used to splice rendered math directly into HTML content, preserving
 * surrounding HTML structure instead of fragmenting into multiple React elements.
 */
function renderInlineMathToHTML(math) {
  // Restore bare LaTeX commands (`approx` → `\approx`), then escape bare `%` (a LaTeX
  // comment char) so "$45% = 0.45$" doesn't render as just "45".
  const rendered = katex.renderToString(escapeBareMathPercent(restoreBareLatexCommands(math)), {
    displayMode: false,
    throwOnError: false
  })
  return `<span class="not-prose">${rendered}</span>`
}

/**
 * Render HTML content that may contain math expressions.
 * Handles both block math ($$...$$ or \[...\]) and inline math ($...$ or \(...\)).
 *
 * Inline math is rendered to KaTeX HTML and spliced in-place, keeping surrounding
 * HTML structure intact. Block math splits into separate React components.
 */
function renderHTMLWithBlockMath(text, sanitizer, Wrapper) {
  // Size cap: math detection over pathological content (e.g. thousands of unpaired '$')
  // is quadratic in the worst case. Above the cap, render sanitized HTML without math
  // splicing instead of hanging the render path.
  if (text.length > MAX_PROCESS_LENGTH) {
    // Bare console.warn: shape pinned by tests (see processContent cap guard).
    // eslint-disable-next-line no-console
    console.warn(
      `renderHTMLWithBlockMath: content length ${text.length} exceeds cap ${MAX_PROCESS_LENGTH}; skipping math detection`
    )
    return <Wrapper dangerouslySetInnerHTML={{ __html: sanitizer(text) }} />
  }

  // Fast path: every math form ($...$, $$...$$, \(...\), \[...\]) requires a '$' or '\'.
  // Without either there can be no matches, so skip the detection passes entirely
  // (identical output to the matches.length === 0 branch below).
  if (!text.includes('$') && !text.includes('\\')) {
    return <Wrapper dangerouslySetInnerHTML={{ __html: sanitizer(text) }} />
  }

  // Collect all math matches
  const allMatches = []
  let m

  // Block math: $$...$$ or \[...\]
  BLOCK_MATH_RE.lastIndex = 0
  while ((m = BLOCK_MATH_RE.exec(text)) !== null) {
    allMatches.push({
      start: m.index,
      end: m.index + m[0].length,
      math: (m[1] || m[2]).trim(),
      isBlock: true
    })
  }

  // Inline math: \(...\)
  PAREN_MATH_RE.lastIndex = 0
  while ((m = PAREN_MATH_RE.exec(text)) !== null) {
    allMatches.push({
      start: m.index,
      end: m.index + m[0].length,
      math: m[1].trim(),
      isBlock: false
    })
  }

  // Inline math: $...$
  const dollarMatches = findDollarMathExpressions(text)
  for (const dm of dollarMatches) {
    allMatches.push({
      start: dm.start,
      end: dm.end,
      math: dm.content.trim(),
      isBlock: false
    })
  }

  // Sort by position and remove overlapping matches
  allMatches.sort((a, b) => a.start - b.start)
  const matches = []
  let lastEnd = 0
  for (const match of allMatches) {
    if (match.start >= lastEnd) {
      matches.push(match)
      lastEnd = match.end
    }
  }

  // No math: fast path
  if (matches.length === 0) {
    return <Wrapper dangerouslySetInnerHTML={{ __html: sanitizer(text) }} />
  }

  const hasBlock = matches.some((match) => match.isBlock)

  // All inline: reverse-splice KaTeX HTML into the string, sanitize, render as single wrapper.
  // This preserves surrounding HTML structure (e.g. <p>The value of $x$ is 5</p>)
  // instead of fragmenting into separate dangerouslySetInnerHTML segments.
  if (!hasBlock) {
    let spliced = text
    for (let i = matches.length - 1; i >= 0; i--) {
      const match = matches[i]
      const rendered = renderInlineMathToHTML(match.math)
      spliced = spliced.slice(0, match.start) + rendered + spliced.slice(match.end)
    }
    return <Wrapper dangerouslySetInnerHTML={{ __html: sanitizer(spliced) }} />
  }

  // Mixed block + inline: forward-iterate with offset tracking.
  // Splice inline math into the HTML string (adjusting positions as the string grows),
  // then split on block math boundaries for separate React components.
  let spliced = text
  let offset = 0
  const adjustedBlockMatches = []

  for (const match of matches) {
    if (match.isBlock) {
      adjustedBlockMatches.push({
        start: match.start + offset,
        end: match.end + offset,
        math: match.math
      })
    } else {
      const rendered = renderInlineMathToHTML(match.math)
      const adjustedStart = match.start + offset
      const adjustedEnd = match.end + offset
      spliced = spliced.slice(0, adjustedStart) + rendered + spliced.slice(adjustedEnd)
      offset += rendered.length - (match.end - match.start)
    }
  }

  // Split on block math boundaries
  const parts = []
  let last = 0
  for (let i = 0; i < adjustedBlockMatches.length; i++) {
    const bm = adjustedBlockMatches[i]
    const htmlBefore = spliced.slice(last, bm.start).trim()
    if (htmlBefore) {
      parts.push(
        <Wrapper key={`h-${i}`} dangerouslySetInnerHTML={{ __html: sanitizer(htmlBefore) }} />
      )
    }
    try {
      parts.push(
        <div key={`m-${i}`} className="not-prose">
          <BlockMath math={escapeBareMathPercent(bm.math)} />
        </div>
      )
    } catch (err) {
      logger.error('Error rendering LaTeX in HTML content:', err)
      parts.push(
        <pre key={`me-${i}`} className="text-red-500 text-sm overflow-x-auto">
          {bm.math}
        </pre>
      )
    }
    last = bm.end
  }
  const trailing = spliced.slice(last).trim()
  if (trailing) {
    parts.push(<Wrapper key="h-trail" dangerouslySetInnerHTML={{ __html: sanitizer(trailing) }} />)
  }

  return parts.length === 1 ? parts[0] : <>{parts}</>
}

/**
 * Pre-extract <img> tags with data: URIs before markdown processing.
 * Large base64 data URIs should not be parsed by the markdown engine.
 */
const IMG_DATA_URI_RE = /<img\s[^>]*src\s*=\s*["']data:[^"']+["'][^>]*\/?>/gi

// Bare `data:image/...;base64,...` strings (embedded directly in content, not inside an <img>) are
// matched by the shared BARE_DATA_URI_RE (shared/content/dataUriImages.js) — the single source of
// truth for this pattern, also used by the server PDF export. Its leading alternation preserves code
// regions and already-<img>-wrapped URIs; the base64 class stops at the first whitespace/newline.

// Exported for stage-level tests (contentPipeline.fixtures.test.js).
export function extractDataUriImages(text) {
  if (!text || typeof text !== 'string' || !text.includes('data:image')) {
    return { cleanText: text || '', images: [] }
  }
  const images = []
  // Pass 1: data URIs already inside an <img> tag.
  IMG_DATA_URI_RE.lastIndex = 0
  let cleanText = text.replace(IMG_DATA_URI_RE, (match) => {
    const placeholder = `<!--IMG_PLACEHOLDER_${images.length}-->`
    images.push(match)
    return placeholder
  })
  // Pass 2: bare data URIs left over. Safe to run after pass 1 because the placeholders contain no
  // `data:`, so wrapped images are never double-matched. Code regions are matched first and preserved
  // so a data URI written as inline/fenced code isn't rewritten into an image.
  BARE_DATA_URI_RE.lastIndex = 0
  cleanText = cleanText.replace(BARE_DATA_URI_RE, (match, codeRegion) => {
    if (codeRegion) return codeRegion
    const placeholder = `<!--IMG_PLACEHOLDER_${images.length}-->`
    images.push(`<img src="${match}" alt="" />`)
    return placeholder
  })
  return { cleanText, images }
}

/**
 * Render markdown content that had data-URI images extracted.
 * Markdown segments go through renderMarkdownWithMath(),
 * image segments go through dangerouslySetInnerHTML with sanitization.
 */
function renderMarkdownWithImages(cleanText, images, sanitizer) {
  const IMG_PLACEHOLDER_RE = /<!--IMG_PLACEHOLDER_(\d+)-->/g
  const segments = []
  let lastIndex = 0
  let match

  IMG_PLACEHOLDER_RE.lastIndex = 0
  while ((match = IMG_PLACEHOLDER_RE.exec(cleanText)) !== null) {
    const textBefore = cleanText.slice(lastIndex, match.index).trim()
    if (textBefore) {
      segments.push({ type: 'md', content: textBefore })
    }
    segments.push({ type: 'img', content: images[parseInt(match[1], 10)] })
    lastIndex = match.index + match[0].length
  }

  const trailing = cleanText.slice(lastIndex).trim()
  if (trailing) {
    segments.push({ type: 'md', content: trailing })
  }

  const parts = segments.map((seg, i) => {
    if (seg.type === 'md') {
      return <React.Fragment key={`md-${i}`}>{renderMarkdownWithMath(seg.content)}</React.Fragment>
    }
    return <div key={`img-${i}`} dangerouslySetInnerHTML={{ __html: sanitizer(seg.content) }} />
  })

  return parts.length === 1 ? parts[0] : <>{parts}</>
}

/**
 * Render a text segment (no shortcodes) based on content type.
 */
function renderSegment(text, contentType, sanitizer, Wrapper) {
  if (!text) return null

  // Wrap bare `data:image/...;base64,...` strings into <img> BEFORE the md/html fork so every
  // branch renders them as a picture (not a base64 text blob) — the single convergence point
  // shared with the server PDF export (shared/content/dataUriImages.js). Safe before detection:
  // <img> is not in the looksLikeHTML set below, so a pure-md field is never misrouted to html;
  // on the md path the wrapped <img> is then caught by extractDataUriImages Pass 1, keeping the
  // base64 out of the markdown engine. Cheap: the helper is indexOf-gated on 'data:image'.
  text = wrapBareDataUriImages(text)

  // Auto-detect HTML content: SVGs, MathML, and common HTML tags must go through
  // the HTML sanitization path. This handles cases where content_type is incorrectly set to 'md'.
  const looksLikeHTML =
    /<svg/i.test(text) ||
    /<math[\s>]/i.test(text) ||
    /<\/?(p|div|table|strong|em|ul|ol|h[1-6])\b/i.test(text)
  const effectiveType =
    contentType?.toLowerCase() !== 'html' && looksLikeHTML ? 'html' : contentType

  switch (effectiveType?.toLowerCase()) {
    case 'html':
      return renderHTMLWithBlockMath(text, sanitizer, Wrapper)
    case 'latex':
      // Size cap: renderMathContent runs $-pair detection (quadratic worst case on
      // pathological input). Above the cap, show the raw text instead of hanging.
      if (text.length > MAX_PROCESS_LENGTH) {
        // Bare console.warn: shape pinned by tests (see processContent cap guard).
        // eslint-disable-next-line no-console
        console.warn(
          `renderSegment(latex): content length ${text.length} exceeds cap ${MAX_PROCESS_LENGTH}; skipping math detection`
        )
        return <Wrapper>{text}</Wrapper>
      }
      return renderMathContent(text)
    case 'md':
    default: {
      // Data-URI images are extracted FIRST (cheap, indexOf-gated) so legitimately large
      // content whose bulk is an embedded base64 image is not penalized by the size cap.
      const { cleanText, images } = extractDataUriImages(text)
      // Size cap on the remaining prose: markdown + math detection over pathological
      // oversized text (e.g. endless '$' runs) must not hang the render path.
      if (cleanText.length > MAX_PROCESS_LENGTH) {
        // Bare console.warn: shape pinned by tests (see processContent cap guard).
        // eslint-disable-next-line no-console
        console.warn(
          `renderSegment(md): content length ${cleanText.length} exceeds cap ${MAX_PROCESS_LENGTH}; rendering as plain text`
        )
        return <Wrapper>{text}</Wrapper>
      }
      if (images.length > 0) {
        return renderMarkdownWithImages(cleanText, images, sanitizer)
      }
      return renderMarkdownWithMath(text)
    }
  }
}

/**
 * Render content based on content_type and content analysis.
 *
 * @param {string} content - The raw content to render
 * @param {string} [contentType='md'] - Content type: 'md', 'html', or 'latex'
 * @param {Function|Object} [sanitizerOrOptions=sanitizeQuestionHTML] - Either a sanitizer function or options object
 * @returns {React.ReactNode|null} Rendered content
 */
export const renderContent = (
  content,
  contentType = 'md',
  sanitizerOrOptions = sanitizeQuestionHTML
) => {
  if (!content) return null

  // Resolve sanitizer - support both function and options object
  let sanitizer
  let wrapperTag = 'div'
  if (typeof sanitizerOrOptions === 'function') {
    sanitizer = sanitizerOrOptions
  } else if (typeof sanitizerOrOptions === 'object' && sanitizerOrOptions !== null) {
    sanitizer = sanitizerOrOptions.sanitizer || sanitizeQuestionHTML
    wrapperTag = sanitizerOrOptions.wrapperTag || 'div'
  } else {
    sanitizer = sanitizeQuestionHTML
  }

  // Ensure content is a string
  const contentStr = typeof content === 'string' ? content : String(content)

  // Run processing pipeline (repair, alttext conversion)
  const processed = processContent(contentStr)

  const Wrapper = wrapperTag

  // Phase 2: Check for and handle shortcodes
  if (hasShortcodes(processed)) {
    const { cleanText, shortcodes } = extractShortcodes(processed)

    // Split clean text on shortcode placeholders and interleave with rendered shortcodes
    const parts = []
    let remaining = cleanText

    for (let i = 0; i < shortcodes.length; i++) {
      const placeholder = `<!--SHORTCODE_${i}-->`
      const placeholderIdx = remaining.indexOf(placeholder)

      if (placeholderIdx >= 0) {
        // Text before the shortcode
        const textBefore = remaining.slice(0, placeholderIdx).trim()
        if (textBefore) {
          parts.push(
            <React.Fragment key={`text-${i}`}>
              {renderSegment(textBefore, contentType, sanitizer, Wrapper)}
            </React.Fragment>
          )
        }

        // The shortcode itself
        parts.push(<ShortcodeRenderer key={`sc-${i}`} config={shortcodes[i]} />)

        remaining = remaining.slice(placeholderIdx + placeholder.length)
      }
    }

    // Remaining text after the last shortcode
    const trailingText = remaining.trim()
    if (trailingText) {
      parts.push(
        <React.Fragment key="text-trailing">
          {renderSegment(trailingText, contentType, sanitizer, Wrapper)}
        </React.Fragment>
      )
    }

    return parts.length === 1 ? parts[0] : <>{parts}</>
  }

  // No shortcodes: render directly
  return renderSegment(processed, contentType, sanitizer, Wrapper)
}

// Re-export commonly used utilities for convenience
export { renderMarkdownWithMath, renderMathContent } from '../components/MathRenderer'
export { isHTML, isLaTeX, detectContentType } from './htmlDetection'
export { sanitizeQuestionHTML, sanitizeAnswerHTML, sanitizeHTML } from './sanitization'
export { repairCorruptedLatex, convertAlttextToLatex, convertMathMLToLatex } from './latexRepair'
